import React, { useState, useEffect } from "react";
import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  ProgressBar,
  Dropdown,
  ListGroup,
  Alert,
} from "react-bootstrap";
import Loader from "../../components/common/Loader";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import {
  People,
  Calendar,
  FileText,
  GraphUp,
  Clock,
  CheckCircle,
  XCircle,
  ExclamationCircle,
  ArrowUp,
  ArrowDown,
  ThreeDots,
  Eye,
  Download,
  Plus,
  QuestionCircle,
  PersonCheck,
  Bell,
} from "react-bootstrap-icons";
import { Line, Doughnut, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentAppointments, setRecentAppointments] = useState([]);
  const [recentStudents, setRecentStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState({});
  const [performanceData, setPerformanceData] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    fetchDashboardData();
    fetchNotifications();

    const iv = setInterval(() => {
      fetchNotifications();
    }, 15000);

    return () => clearInterval(iv);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch all data in parallel
      const [statsResponse, appointmentsResponse, studentsResponse] =
        await Promise.all([
          api.get("/admin/dashboard"),
          api.get("/appointments/admin?limit=5&sort=-createdAt"),
          api.get("/students/admin/all?limit=5&sort=-createdAt"),
        ]);

      if (statsResponse.data.success) {
        setStats(statsResponse.data.data.stats);
        // pass monthly growth to chart generator
        const monthlyGrowth = statsResponse.data.data.monthlyGrowth || [];
        // recent appointments are used to build appointment distribution
        const recentApts = appointmentsResponse.data.success
          ? appointmentsResponse.data.appointments.slice(0, 5)
          : [];

        generateChartData(monthlyGrowth, recentApts);
        generatePerformanceData(statsResponse.data.data.stats);
      }

      if (appointmentsResponse.data.success) {
        setRecentAppointments(
          appointmentsResponse.data.appointments.slice(0, 5),
        );
      }

      if (studentsResponse.data.success) {
        setRecentStudents(studentsResponse.data.students.slice(0, 5));
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const generateChartData = (monthlyGrowth = [], recentApts = []) => {
    // Build student growth from monthlyGrowth returned by backend
    const labels = monthlyGrowth.map((m) => m.month) || [];
    const data = monthlyGrowth.map((m) => m.count) || [];

    const studentChartData = {
      labels: labels.length
        ? labels
        : ["Jan", "Feb", "Mar", "Apr", "May", "Jun"],
      datasets: [
        {
          label: "New Students",
          data: data.length ? data : [12, 19, 8, 15, 22, 18],
          borderColor: "#4f46e5",
          backgroundColor: "rgba(79, 70, 229, 0.12)",
          tension: 0.3,
          fill: true,
          pointRadius: 3,
        },
      ],
    };

    // Appointment distribution computed from recent appointments (best-effort)
    const counts = { zoom: 0, office: 0, assessment: 0 };
    (recentApts || []).forEach((a) => {
      if (!a) return;
      const t = a.appointmentType || a.type || "";
      if (t === "zoom") counts.zoom++;
      else if (t === "office") counts.office++;
      else if (t === "assessment") counts.assessment++;
    });

    const appointmentChartData = {
      labels: ["Phone Call", "Office", "Assessment"],
      datasets: [
        {
          data: [counts.zoom, counts.office, counts.assessment],
          backgroundColor: ["#3b82f6", "#10b981", "#8b5cf6"],
          borderWidth: 1,
        },
      ],
    };

    setChartData({
      studentGrowth: studentChartData,
      appointmentDistribution: appointmentChartData,
    });
  };

  const generatePerformanceData = (statsData = {}) => {
    setPerformanceData({
      conversionRate: statsData.conversionRate ?? 0,
      satisfactionScore: statsData.satisfactionScore ?? 0,
      avgResponseTime: statsData.avgResponseTime || "—",
      completionRate: statsData.completionRate ?? statsData.successRate ?? 0,
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return (
          <Badge bg="success" className="d-flex align-items-center gap-1">
            <CheckCircle size={12} /> Confirmed
          </Badge>
        );
      case "pending":
        return (
          <Badge bg="warning" className="d-flex align-items-center gap-1">
            <Clock size={12} /> Pending
          </Badge>
        );
      case "cancelled":
        return (
          <Badge bg="danger" className="d-flex align-items-center gap-1">
            <XCircle size={12} /> Cancelled
          </Badge>
        );
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  const getTypeBadge = (type) => {
    switch (type) {
      case "zoom":
        return <Badge bg="info">Phone Call</Badge>;
      case "office":
        return <Badge bg="primary">Office</Badge>;
      case "assessment":
        return <Badge bg="secondary">Assessment</Badge>;
      default:
        return <Badge bg="secondary">{type}</Badge>;
    }
  };

  const getScoreBadge = (score) => {
    if (score >= 140) return <Badge bg="success">High</Badge>;
    if (score >= 100) return <Badge bg="warning">Medium</Badge>;
    return <Badge bg="danger">Low</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleRefresh = () => {
    fetchDashboardData();
  };

  const fetchNotifications = async () => {
    try {
      const res = await api.get("/admin/notifications");
      if (res.data.success) {
        setNotifications(res.data.notifications || []);
        setUnreadCount((res.data.notifications || []).length);
      }
    } catch (err) {
      // silently ignore
      console.error("Notifications fetch error:", err);
    }
  };

  const markAsRead = async (notification) => {
    try {
      if (notification.type === "appointment") {
        await api.post(
          `/admin/notifications/appointment/${notification.appointmentMongoId}/mark-read`,
        );
      } else {
        // default to assessment / student
        await api.post(
          `/admin/notifications/${notification.studentId}/mark-read`,
        );
      }
      setNotifications((prev) => prev.filter((n) => n._id !== notification._id));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error("Mark read error:", err);
    }
  };

  const handleNotificationClick = async (notification) => {
    try {
      // navigate to relevant page
      if (notification.type === "appointment" && notification.appointmentMongoId) {
        navigate(`/admin/appointments/${notification.appointmentMongoId}`);
      } else if (notification.studentId) {
        // assessment notification or appointment with linked student
        navigate(`/admin/students/${notification.studentId}`);
      }

      // mark as read in background
      await markAsRead(notification);
    } catch (err) {
      console.error("Notification click error:", err);
    }
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          padding: 20,
          usePointStyle: true,
        },
      },
    },
  };

  if (loading) return <Loader />;

  return (
    <div style={{ paddingLeft: "20px" }}>
      {/* Header with Refresh Button */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1 fw-bold text-gradient">Dashboard Overview</h2>
          <p className="text-muted mb-0">
            Welcome back! Here's what's happening with your platform.
          </p>
        </div>
        <div className="d-flex gap-2 align-items-center">
          <Dropdown align="end">
            <Dropdown.Toggle variant="link" className="text-muted p-0">
              <div style={{ position: "relative" }}>
                <Bell size={22} />
                {unreadCount > 0 && (
                  <Badge
                    bg="danger"
                    pill
                    style={{
                      position: "absolute",
                      top: -6,
                      right: -6,
                      fontSize: 10,
                      boxShadow: "0 0 0 2px #fff",
                    }}
                  >
                    {unreadCount}
                  </Badge>
                )}
              </div>
            </Dropdown.Toggle>
            <Dropdown.Menu className="p-0" style={{ minWidth: 360 }}>
              <div
                className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center"
                style={{ background: "#f8fafc" }}
              >
                <div className="d-flex align-items-center gap-2">
                  <span
                    className="rounded-circle d-inline-flex align-items-center justify-content-center"
                    style={{
                      width: 28,
                      height: 28,
                      background: "rgba(59,130,246,0.08)",
                    }}
                  >
                    <Bell size={16} className="text-primary" />
                  </span>
                  <div>
                    <div className="fw-semibold">Notifications</div>
                    <small className="text-muted">
                      {unreadCount > 0
                        ? `${unreadCount} new notification${unreadCount > 1 ? "s" : ""}`
                        : "You are all caught up"}
                    </small>
                  </div>
                </div>
              </div>

              <div
                style={{
                  maxHeight: 360,
                  overflowY: "auto",
                  background: "#ffffff",
                }}
              >
                {notifications.length === 0 && (
                  <div className="px-3 py-4 text-center text-muted">
                    <div className="mb-1 fw-medium">No new activity</div>
                    <small style={{ fontSize: 12 }}>
                      New assessments and bookings will appear here.
                    </small>
                  </div>
                )}

                {notifications.map((n) => {
                  const isAppointment = n.type === "appointment";
                  const timestamp =
                    n.timestamp &&
                    new Date(n.timestamp).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    });

                  const pillBg = isAppointment ? "bg-primary" : "bg-success";
                  const pillText = isAppointment ? "Appointment" : "Assessment";

                  return (
                    <button
                      key={n._id}
                      type="button"
                      className="w-100 border-0 text-start px-3 py-2 notification-item-btn"
                      onClick={() => handleNotificationClick(n)}
                      style={{
                        background: "transparent",
                      }}
                    >
                      <div className="d-flex gap-2">
                        <div
                          className="flex-shrink-0 d-flex align-items-start justify-content-center rounded-circle"
                          style={{
                            width: 34,
                            height: 34,
                            background: isAppointment
                              ? "rgba(59,130,246,0.08)"
                              : "rgba(16,185,129,0.08)",
                          }}
                        >
                          {isAppointment ? (
                            <Calendar size={16} className="text-primary mt-1" />
                          ) : (
                            <FileText size={16} className="text-success mt-1" />
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <div className="fw-semibold" style={{ fontSize: 13 }}>
                              {isAppointment
                                ? "New appointment booking"
                                : "New assessment submitted"}
                            </div>
                            <span
                              className={`badge rounded-pill ${pillBg} bg-opacity-10 text-capitalize`}
                              style={{
                                fontSize: 10,
                                color: isAppointment ? "#1d4ed8" : "#047857",
                              }}
                            >
                              {pillText}
                            </span>
                          </div>
                          <div className="mb-1" style={{ fontSize: 12 }}>
                            <span className="fw-medium">{n.name}</span>
                            {n.email && (
                              <span className="text-muted"> · {n.email}</span>
                            )}
                          </div>

                          {isAppointment && n.appointmentId && (
                            <div className="text-muted" style={{ fontSize: 12 }}>
                              #{n.appointmentId}
                              {n.scheduledDate && " · "}
                              {n.scheduledDate &&
                                new Date(n.scheduledDate).toLocaleDateString(
                                  "en-US",
                                  {
                                    month: "short",
                                    day: "numeric",
                                  },
                                )}
                              {n.scheduledTime ? ` at ${n.scheduledTime}` : ""}
                            </div>
                          )}

                          {!isAppointment &&
                            typeof n.exactPercentage === "number" && (
                              <div className="text-muted" style={{ fontSize: 12 }}>
                                Assessment score: {n.exactPercentage}%
                              </div>
                            )}

                          {timestamp && (
                            <div
                              className="text-muted mt-1"
                              style={{ fontSize: 11 }}
                            >
                              {timestamp}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="px-3 py-2 border-top text-center bg-light">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={handleRefresh}
                >
                  Refresh dashboard
                </Button>
              </div>
            </Dropdown.Menu>
          </Dropdown>

          <Button variant="outline-primary" size="sm" onClick={handleRefresh}>
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards with Icons */}
      <Row className="mb-4 g-3">
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="bg-primary bg-opacity-10 p-3 rounded">
                  <People size={24} className="text-primary" />
                </div>
                <Badge bg="light" text="dark" className="fs-7">
                  <ArrowUp size={12} className="text-success me-1" />
                  12%
                </Badge>
              </div>
              <h3 className="mb-2">{stats?.totalStudents || 0}</h3>
              <p className="text-muted mb-0">Total Students</p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="bg-info bg-opacity-10 p-3 rounded">
                  <Calendar size={24} className="text-info" />
                </div>
                <Badge bg="light" text="dark" className="fs-7">
                  <ArrowUp size={12} className="text-success me-1" />
                  8%
                </Badge>
              </div>
              <h3 className="mb-2">{stats?.totalAppointments || 0}</h3>
              <p className="text-muted mb-0">Total Appointments</p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="bg-warning bg-opacity-10 p-3 rounded">
                  <FileText size={24} className="text-warning" />
                </div>
                <Badge bg="light" text="dark" className="fs-7">
                  <ArrowDown size={12} className="text-danger me-1" />
                  3%
                </Badge>
              </div>
              <h3 className="mb-2">{stats?.pendingDocuments || 0}</h3>
              <p className="text-muted mb-0">Pending Documents</p>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div className="bg-success bg-opacity-10 p-3 rounded">
                  <GraphUp size={24} className="text-success" />
                </div>
                <Badge bg="light" text="dark" className="fs-7">
                  <ArrowUp size={12} className="text-success me-1" />
                  5%
                </Badge>
              </div>
              <h3 className="mb-2">{stats?.successRate || 0}%</h3>
              <p className="text-muted mb-0">Success Rate</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts and Data Visualization */}
      <Row className="mb-4 g-3">
        <Col lg={8}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-transparent border-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Student Growth Trend</h5>
                <Dropdown>
                  <Dropdown.Toggle variant="link" className="text-muted p-0">
                    <ThreeDots size={20} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    <Dropdown.Item>Export as PDF</Dropdown.Item>
                    <Dropdown.Item>Export as CSV</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item>View Details</Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </Card.Header>
            <Card.Body>
              <div style={{ height: "300px" }}>
                {chartData.studentGrowth && (
                  <Line
                    data={chartData.studentGrowth}
                    options={{
                      ...chartOptions,
                      plugins: {
                        ...chartOptions.plugins,
                        title: {
                          display: false,
                        },
                      },
                    }}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-transparent border-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Appointment Distribution</h5>
              </div>
            </Card.Header>
            <Card.Body className="d-flex align-items-center justify-content-center">
              <div style={{ width: "250px", height: "250px" }}>
                {chartData.appointmentDistribution && (
                  <Doughnut
                    data={chartData.appointmentDistribution}
                    options={{
                      ...chartOptions,
                      cutout: "70%",
                    }}
                  />
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Activities */}
      <Row className="g-3">
        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-transparent border-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Appointments</h5>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush">
                {recentAppointments.map((apt) => (
                  <ListGroup.Item key={apt._id} className="border-0 px-4 py-3">
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-medium">
                          {apt.studentId?.name || "Anonymous Student"}
                        </div>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <small className="text-muted">
                            <Calendar size={12} className="me-1" />
                            {formatDate(apt.scheduledDate)}
                          </small>
                          <small>{getTypeBadge(apt.appointmentType)}</small>
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="fw-medium">{apt.appointmentId}</div>
                        <div className="mt-1">{getStatusBadge(apt.status)}</div>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
                {recentAppointments.length === 0 && (
                  <ListGroup.Item className="border-0 px-4 py-3 text-center text-muted">
                    No recent appointments
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Header className="bg-transparent border-0">
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Students</h5>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <ListGroup variant="flush">
                {recentStudents.map((student) => (
                  <ListGroup.Item
                    key={student._id}
                    className="border-0 px-4 py-3"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <div className="fw-medium">{student.name}</div>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <small className="text-muted">{student.email}</small>
                          {student.totalScore && (
                            <small>{getScoreBadge(student.totalScore)}</small>
                          )}
                        </div>
                      </div>
                      <div className="text-end">
                        <div className="fw-medium">{student.appointmentId}</div>
                        <small className="text-muted">
                          {formatDate(student.createdAt)}
                        </small>
                      </div>
                    </div>
                  </ListGroup.Item>
                ))}
                {recentStudents.length === 0 && (
                  <ListGroup.Item className="border-0 px-4 py-3 text-center text-muted">
                    No recent students
                  </ListGroup.Item>
                )}
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* System Status */}
      <Alert variant="light" className="border mt-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <ExclamationCircle size={20} className="text-primary me-2" />
            <span className="fw-medium">System Status:</span>
            <Badge bg="success" className="ms-2">
              All Systems Operational
            </Badge>
          </div>
          <small className="text-muted">
            Last updated: {new Date().toLocaleTimeString()}
          </small>
        </div>
      </Alert>

      {/* Add custom CSS for colors */}
      <style jsx>{`
        .bg-purple {
          background-color: #8b5cf6 !important;
        }
        .text-purple {
          color: #8b5cf6 !important;
        }
        .bg-orange {
          background-color: #f59e0b !important;
        }
        .text-orange {
          color: #f59e0b !important;
        }
        .bg-teal {
          background-color: #14b8a6 !important;
        }
        .text-teal {
          color: #14b8a6 !important;
        }
        .bg-pink {
          background-color: #ec4899 !important;
        }
        .text-pink {
          color: #ec4899 !important;
        }
        .fs-7 {
          font-size: 0.875rem !important;
        }

        .btn-outline-primary:hover,
        .btn-outline-success:hover,
        .btn-outline-warning:hover,
        .btn-outline-info:hover {
          transform: translateY(-2px);
          transition: transform 0.2s ease;
        }

        .progress-bar-purple {
          background-color: #8b5cf6 !important;
        }

        .progress-bar-pink {
          background-color: #ec4899 !important;
        }
      `}</style>
    </div>
  );
};

export default AdminDashboard;
